/**
 * **数据就绪度 UI**（2026-10-05 第十七批 · 用户确认的口径）。
 *
 * 两个组件，各司其职：
 *
 *  1. `DataReadinessNote`：时间选择器附近的**一行小字**——
 *     「数据齐到 10-03 · 10-04/10-05 仍在结算」。**每个视图都要有**（概览 / 广告管理 /
 *     货号看板 / 广告指标表），数据源是真实的 `/v1/data-readiness`；
 *  2. `SettlementWarning`：用户**主动选到今天/昨天（或任何晚于就绪右端的区间）**时才出现
 *     的**提示条**（warning 语义色，**不是红色错误**）：
 *     「10-04、10-05 仍在结算 —— Meta 01:00 齐、Google 05:30 齐；当前显示的该区间数字可能偏低」。
 *
 * 三条纪律：
 *  · **不藏数字**：只标注，不把用户想看的今天/昨天数据藏起来；
 *  · **不硬编码日期/颜色**：日期来自就绪度纯函数，颜色走既有 token；
 *  · **加载 / 失败 / 空态齐全**：失败给可重试的小字，不假装"数据已齐"。
 */

import type { ReactElement } from "react";
import { describeError } from "../hooks/useApiResource.js";
import type { ViewReadiness } from "../hooks/useDataReadiness.js";

/**
 * 时区如实说明（**必须显示**，2026-10-05 用户口径）。
 *
 * D-2 只保证"**每个平台各自**都是 N 个整天"，**不保证**各平台的 N 天覆盖同一个
 * 24 小时窗口：Meta / Google 的"日"按各自账户时区、Shopify 按店铺时区，
 * 生产实测 17 对连接里 8 对时区标识不同（对齐的那 9 对才是同一窗口）。
 */
export const READINESS_TIMEZONE_CAVEAT =
  "就绪右端只保证各平台各自都是完整的整天；各平台按自己的账户/店铺时区切日，" +
  "时区不一致时这些整天并不覆盖同一个 24 小时窗口（本次不修时区，只做结算闸门）。";

/** `YYYY-MM-DD` → `MM-DD`（跨年时完整日期仍在 tooltip / 明细里）。 */
export function formatReadinessDay(date: string): string {
  return date.length >= 10 ? date.slice(5) : date;
}

function joinDays(dates: readonly string[]): string {
  return dates.map(formatReadinessDay).join("、");
}

/** 提示条触发时，平台「几点齐」的短语（如「Meta 01:00 齐、Google 05:30 齐」）。 */
function settlePhrase(readiness: ViewReadiness): string {
  if (readiness.settlingNotes.length === 0) return "";
  return readiness.settlingNotes.map((note) => `${note.label} ${note.settleClock} 齐`).join("、");
}

export interface DataReadinessNoteProps {
  readonly readiness: ViewReadiness;
  readonly className?: string;
  readonly testId?: string;
}

/** 时间选择器附近的「数据齐到哪天」小字（加载 / 失败 / 就绪三态齐全）。 */
export function DataReadinessNote({ readiness, className, testId }: DataReadinessNoteProps): ReactElement {
  const classes = className ?? "mt-1 text-meta text-ink-3";
  if (readiness.state === "loading" && readiness.payload === null) {
    return (
      <p className={classes} data-testid={testId} data-state="loading">
        正在读取数据就绪度…
      </p>
    );
  }
  if (readiness.state === "error" && readiness.payload === null) {
    return (
      <p className={classes} data-testid={testId} data-state="error">
        数据就绪度读取失败：{describeError(readiness.error)}{" "}
        <button type="button" className="underline" onClick={readiness.reload}>
          重试
        </button>
      </p>
    );
  }
  const readinessData = readiness.readiness;
  if (readinessData === null) {
    return (
      <p className={classes} data-testid={testId} data-state="empty">
        数据就绪度：暂无平台状态（未接入任何平台）。
      </p>
    );
  }
  const tooltip = [...readinessData.platformNotes, readinessData.summary, READINESS_TIMEZONE_CAVEAT].join("\n");
  return (
    <p
      className={classes}
      data-testid={testId}
      data-state="ready"
      data-ready-through={readinessData.readyThroughDate ?? ""}
      title={tooltip}
    >
      {/*
        任务 4：文案统一走 `unsettledWindowNote`（默认口径与「含未结算」口径同一纯函数），
        因此「数据齐到 X；10-04、10-05 仍在结算」在每个视图里逐字一致。
        默认口径下窗口右端 = 就绪右端，仍在结算的日子是**窗口之后**的日子。
      */}
      {readinessData.readyThroughDate === null ? (
        <>数据齐到哪天：<b>无法确认</b>（{readinessData.summary}）</>
      ) : (
        <span
          className={
            readiness.unsettledNote.showsUnsettledWarning
              ? "text-danger-text"
              : (readiness.noteReadiness?.pendingDates.length ?? 0) > 0 ||
                  (readiness.noteReadiness?.missingDates.length ?? 0) > 0
                ? "text-warning-text"
                : undefined
          }
        >
          {readiness.unsettledNote.label}
        </span>
      )}
    </p>
  );
}

export interface SettlementWarningProps {
  readonly readiness: ViewReadiness;
  readonly className?: string;
}

/**
 * 「仍在结算」提示条。**只在** `readiness.showsSettlementWarning` 时渲染；
 * 语义色是 warning（不是 error），因为这不是故障——是平台的结算节奏。
 */
export function SettlementWarning({ readiness, className }: SettlementWarningProps): ReactElement | null {
  if (!readiness.showsSettlementWarning || readiness.readiness === null) return null;
  const data = readiness.readiness;
  const phrase = settlePhrase(readiness);
  const classes =
    className ?? "mt-3 rounded-lg border border-warning/40 bg-warning-subtle px-3 py-2 text-2xs text-warning-text";
  return (
    <div className={classes} role="status" data-testid="settlement-warning">
      {data.readyThroughDate === null ? (
        <p>
          <b>数据就绪度无法确认</b>（{data.summary}）—— 当前区间显示的
          <span className="tnum"> {readiness.range.from} → {readiness.range.to} </span>
          数字可能不完整，请以「数据齐到哪天」为准。
        </p>
      ) : data.pendingDates.length > 0 ? (
        <p>
          <span className="tnum font-medium">{joinDays(data.pendingDates)}</span> 仍在结算
          {phrase === "" ? "" : ` —— ${phrase}`}；当前显示的该区间
          <span className="tnum"> {readiness.range.from} → {readiness.range.to} </span>
          数字可能偏低。
        </p>
      ) : null}
      {data.missingDates.length > 0 ? (
        <p className="mt-1">
          ⚠️ 另有 <span className="tnum font-medium">{joinDays(data.missingDates)}</span>{" "}
          已过平台预期产出时间却仍没有数据——这是<b>真实缺口</b>（可能是同步停摆），
          不是「还在结算」。
        </p>
      ) : null}
      <p className="mt-1 opacity-80">{READINESS_TIMEZONE_CAVEAT}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 「含未结算」开关 + 醒目警告（2026-10-05 第二十三批 · 任务 4）
// ---------------------------------------------------------------------------

export interface UnsettledToggleProps {
  /** 当前是否开启（来自 URL 的 `includeUnsettled`）。 */
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  /** 用于 tooltip 的窗口标注（由 `useDataReadiness` 的同名纯函数给出）。 */
  readonly readiness: ViewReadiness;
  readonly testId?: string;
}

/**
 * 「含未结算」开关（进 URL、可分享）。默认关闭 = 保守口径（右端=就绪右端）。
 *
 * 打开后窗口右端切到**今天**，并触发 `UnsettledIncludeWarning`。
 * 语义刻意做成可分享链接：`includeUnsettled=1` 直接还原同一窗口。
 */
export function UnsettledToggle({ checked, onChange, readiness, testId }: UnsettledToggleProps): ReactElement {
  return (
    <label
      className="inline-flex cursor-pointer select-none items-center gap-1 text-meta text-ink-3"
      title={`${readiness.unsettledNote.label}\n默认口径：${readiness.readyThroughDate ?? "无法确认"}`}
      data-testid={testId}
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 accent-accent"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId === undefined ? undefined : `${testId}-checkbox`}
      />
      含未结算日（右端=今天）
    </label>
  );
}

export interface UnsettledIncludeWarningProps {
  readonly readiness: ViewReadiness;
  readonly className?: string;
}

/**
 * 「含未结算」开启时的**醒目**警告（危险色，区别于结算提示条的 warning）：
 * 用户明确要看未结算日 → 必须写清「数字会随结算变化，默认口径不含这些日子」。
 */
export function UnsettledIncludeWarning({ readiness, className }: UnsettledIncludeWarningProps): ReactElement | null {
  if (!readiness.includeUnsettled || !readiness.unsettledNote.showsUnsettledWarning) return null;
  const data = readiness.readiness;
  const classes =
    className ?? "mt-3 rounded-lg border border-danger/40 bg-danger-subtle px-3 py-2 text-2xs text-danger-text";
  return (
    <div className={classes} role="alert" data-testid="unsettled-include-warning">
      <p>
        <b>含未结算日</b>：窗口右端 = <span className="tnum">{readiness.unsettledNote.rangeEnd}</span>
        {data !== null && data.readyThroughDate !== null ? (
          <>
            （数据齐到 <span className="tnum">{data.readyThroughDate}</span>
            {data.pendingDates.length > 0 ? <>；<span className="tnum">{joinDays(data.pendingDates)}</span> 仍在结算</> : null}）
          </>
        ) : (
          <>（数据就绪度无法确认）</>
        )}
        。<b>数字会随结算变化</b>；默认口径（保守右端）不含这些日子，切换开关即恢复。
      </p>
    </div>
  );
}
